<template>
  <div class="card mb-2">
    <div class="card-body bg-secondary pt-2 pb-0 pl-1 pr-2">
      <b-form class="search-form" @submit.prevent="onSearch">
        <div class="d-flex">
          <div class="row align-items-center">
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('customer_uuid')">
              <customer-select-input-widget @unselect="onCustomerUnselect" @select="onCustomerSelect" :label="false"
                                            is-search
                                            :options="{placeholder: $t('Customer')}" :model="searchModel"
                                            attribute="customer_uuid"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('receipt')">
              <receipt-select-input-widget @unselect="onReceiptUnselect" @select="onReceiptSelect" :model="searchModel"
                                           attribute="receipt_uuid" remove-duplicates multiple
                                           :label="false" :options="{placeholder: $t('Receipt'), multiple: true}"
                                           :customer-uuid="this.searchModel.customer_uuid"
                                           :items="searchModel.receipts"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('item_no')">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="item_no"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('grouped_item_no') && enableGroupedItems">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="grouped_item_no"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('company_name')">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="company_name"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('date')">
              <b-form-group class="mb-0">
                <date-range-picker
                  opens="center"
                  style="width: 100%"
                  @update="onDateRangeApply"
                  appendToBody
                  :calculatePosition="calculatePosition"
                  showWeekNumbers
                  showDropdowns
                  ref="picker"
                  :locale-data="{ firstDay: 1, format: 'DD-MM-YYYY HH:mm:ss' }"
                  v-model="dateRange">
                  <template v-slot:input="picker" style="min-width: 350px;">
                    <span v-if="!picker.startDate" class="text-muted">
                      {{ $t('Date Range') }}
                    </span>
                    <span v-else>
                      {{ picker.startDate | toDate }} - {{ picker.endDate | toDate }}
                    </span>
                  </template>
                </date-range-picker>
              </b-form-group>
            </div>
            <div class="input-wrapper">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="title"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('sale_search')">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="sale_search"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('lot_no')">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="lot_no"/>
            </div>
            <div class="input-wrapper">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="collector"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('department_uuid')">
              <department-select-input-widget @select="onDepartmentSelect"
                                              @unselect="onDepartmentUnselect"
                                              :label="false"
                                              :options="{placeholder: $t('Department')}"
                                              :model="searchModel"
                                              attribute="department_uuid"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('category_uuid')">
              <category-select-input-widget @select="onCategorySelect"
                                            :label="false" with-all-categories :department="searchModel.department"
                                            :options="{placeholder: $t('Category')}"
                                            :model="searchModel"
                                            attribute="category_uuid"/>
            </div>
            <div class="input-wrapper">
              <input-widget :model="searchModel" attribute="status" value-field="value" display-field="${item.key}"
                            type="select2" :label="false" :options="{placeholder: $t('Status')}"
                            :multiple="true" :items="statuses"/>
            </div>
            <div class="input-wrapper">
              <user-select-input-widget @select="onSpecialistSelect"
                                        :model="searchModel"
                                        :label="false"
                                        :options="{placeholder: $t('Specialist')}"
                                        attribute="specialist_uuid" is-search/>
            </div>
            <div class="input-wrapper">
              <cataloguing-lifecycle-select-input-widget attribute="cataloguing_lifecycle" :model="searchModel"
                                                         :label="false" :options="{placeholder: $t('Cataloguing Lifecycle')}"
                                                         :multiple="true" />
            </div>
            <div class="input-wrapper">
              <photography-lifecycle-select-input-widget attribute="photography_lifecycle" :model="searchModel"
                            :label="false" :options="{placeholder: $t('Photography Lifecycle')}"
                            :multiple="true"/>
            </div>
            <div class="input-wrapper" v-if="availableFilterFields && availableFilterFields.includes('asset_number')">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="asset_number"/>
            </div>
            <div class="input-wrapper" v-if="availableFilterFields && availableFilterFields.includes('serial_number')">
              <input-widget :label="false" :placeholder="true" :model="searchModel" attribute="serial_number"/>
            </div>
            <div class="input-wrapper">
              <user-select-input-widget
                v-if="!excludeSearchFields.includes('account_manager_uuid')" :model="searchModel" :label="false"
                is-search attribute="account_manager_uuid" :options="{placeholder: $t('Account Manager')}"
                @select="onUserSelect">
              </user-select-input-widget>
            </div>
            <div class="input-wrapper">
              <sale-select-input-widget @unselect="onLatestAuctionSelect" :model="searchModel"
                                        attribute="latest_auction_uuid" remove-duplicates multiple
                                        :label="false" :options="{placeholder: $t('Latest Auction'), multiple: true}"
                                        :latest-receipt-uuids="receiptUuids" remove-title-from-selection
                                        :items="searchModel.receipts"/>
            </div>
            <div class="input-wrapper">
              <input-widget :model="searchModel" attribute="latest_status" value-field="value"
                            display-field="${item.key}"
                            type="select2" :label="false" :options="{placeholder: $t('Latest Lot Status')}"
                            :multiple="true" :items="lotStatuses"/>
            </div>
            <div class="input-wrapper">
              <reserve-type-select-input-widget :model="searchModel" attribute="reserve_type"
                                                :options="{placeholder: $t('Reserve Type')}" :label="false"/>
            </div>
            <template v-if="inventoryDynamicFieldsFilters.length > 0">
              <div class="input-wrapper" v-for="field in inventoryDynamicFieldsFilters" :key="field.key">
                <input-widget :label="getDynamicFieldFilterLabel(field)" :placeholder="field.label" :model="searchModel"
                              :attribute="field.key" :type="field.type"/>
              </div>
            </template>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('collected_status')">
              <input-widget :model="searchModel" attribute="collected_status" type="select2"
                            :items="contractedStatus"
                            value-field="key" :label="false" :options="{placeholder: $t('Collected Status')}"
                            display-field="${item.text}">
              </input-widget>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('hammer_min')">
              <currency-input-widget :currency="computedCurrencySymbol" :model="searchModel" attribute="hammer_min"
                                     :label="false" :placeholder="true"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('hammer_max')">
              <currency-input-widget :currency="computedCurrencySymbol" :model="searchModel" attribute="hammer_max"
                                     :label="false" :placeholder="true"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('created_by')">
              <user-select-input-widget attribute="created_by_uuid" @select="onCreatedBySelect"
                                        :model="searchModel"
                                        :label="false"
                                        :options="{placeholder: $t('Created By')}" is-search/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('item_location_uuid')">
              <item-location-select-input-widget :model="searchModel" attribute="item_location_uuid" :label="false"
                                                 :placeholder="$t('Item Location')"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('proposal_status')">
              <input-widget :model="searchModel" attribute="proposal_status" type="select2"
                            value-field="value" display-field="${item.key}" :label="false"
                            :options="{placeholder: $t('Proposal Status')}" :items="proposalStatusOptions"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('proposal_suggestion')">
              <input-widget :model="searchModel" attribute="proposal_suggestion" type="select2"
                            value-field="value" display-field="${item.key}" :label="false"
                            :options="{placeholder: $t('Proposal Suggestion')}" :items="suggestionOptions"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('images_value')">
              <input-widget :model="searchModel" attribute="images_value" type="select2"
                            value-field="value" display-field="${item.key}" :label="false"
                            :options="{placeholder: $t('Images')}" :items="imagesValues"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('tax_status_uuid')">
              <tax-status-select-input-widget :model="searchModel" attribute="tax_status_uuid" :label="false"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('transferred_from_branch_uuid')">
              <input-widget :model="searchModel" attribute="transferred_from_branch_uuid" value-field="uuid"
                            display-field="${item.name}" type="select2" :label="false"
                            :options="{placeholder: $t('Transferred From Branch')}" :items="usersOtherBranches"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('transferred_to_branch_uuid')">
              <input-widget :model="searchModel" attribute="transferred_to_branch_uuid" value-field="uuid"
                            display-field="${item.name}" type="select2" :label="false"
                            :options="{placeholder: $t('Transferred To Branch')}" :items="usersOtherBranches"
                            :disabled="!searchModel.is_archived"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('rep_user_uuid')">
              <user-select-input-widget attribute="rep_user_uuid" @select="onRepUserSelect"
                                        :model="searchModel"
                                        :label="false"
                                        :options="{placeholder: $t('Representative User')}"
                                        is-search/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('is_archived')">
              <input-widget :model="searchModel" attribute="is_archived" type="checkbox"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('available_items_only')">
              <input-widget :model="searchModel" attribute="available_items_only" type="checkbox"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('is_draft') && enableInventoryDraft">
              <input-widget :model="searchModel" attribute="is_draft" type="checkbox"/>
            </div>
            <div class="input-wrapper">
              <input-widget :model="searchModel" attribute="has_internal_images" type="checkbox"/>
            </div>
            <div class="input-wrapper">
              <input-widget :model="searchModel" attribute="has_attachments" type="checkbox"/>
            </div>
          </div>
          <div class="search-btn-wrapper">
            <b-button :disabled="loading" variant="primary" @click.prevent="onResetClick">
              <font-awesome-icon icon="undo" class="icon"/>
              {{ $t('Reset') }}
            </b-button>
            <b-button :disabled="loading" type="submit" variant="primary" class="ml-2">
              <font-awesome-icon icon="search" class="icon"/>
              {{ $t('Search') }}
            </b-button>
          </div>
        </div>
      </b-form>
    </div>
  </div>
</template>

<script>
  import DateRangePicker from 'vue2-daterange-picker'
  import InputWidget from "../../core/components/input-widget/InputWidget";
  import DepartmentSelectInputWidget from "../../core/widgets/DepartmentSelectInputWidget";
  import CustomerSelectInputWidget from "../../core/widgets/CustomerSelectInputWidget";
  import {createNamespacedHelpers} from "vuex";
  import i18n from "@/shared/i18n";
  import UserSelectInputWidget from "../../core/widgets/UserSelectInputWidget";
  import CategorySelectInputWidget from "@/core/widgets/CategorySelectInputWidget";
  import ReceiptSelectInputWidget from "@/core/widgets/ReceiptSelectInputWidget";
  import SaleSelectInputWidget from "@/core/widgets/SaleSelectInputWidget";
  import ReserveTypeSelectInputWidget from "@/core/widgets/ReserveTypeSelectInputWidget";
  import CurrencyInputWidget from "@/core/widgets/CurrencyInputWidget.vue";
  import ItemLocationSelectInputWidget from "@/core/widgets/ItemLocationSelectInputWidget";
  import TaxStatusSelectInputWidget from "@/core/widgets/TaxStatusSelectInputWidget";
  import CataloguingLifecycleSelectInputWidget from "@/core/widgets/CataloguingLifecycleSelectInputWidget.vue";
  import PhotographyLifecycleSelectInputWidget from "@/core/widgets/PhotographyLifecycleSelectInputWidget.vue";

  const {mapActions, mapState} = createNamespacedHelpers('inventory');
  const {mapState: unsoldProposalsMapState} = createNamespacedHelpers('vendors/unsoldProposals');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "InventorySearch",
    components: {
      PhotographyLifecycleSelectInputWidget,
      CataloguingLifecycleSelectInputWidget,
      TaxStatusSelectInputWidget,
      ItemLocationSelectInputWidget,
      CurrencyInputWidget,
      ReserveTypeSelectInputWidget,
      SaleSelectInputWidget,
      ReceiptSelectInputWidget,
      CategorySelectInputWidget,
      UserSelectInputWidget,
      CustomerSelectInputWidget, DepartmentSelectInputWidget, InputWidget, DateRangePicker
    },
    props: {
      excludeSearchFields: {
        type: Array,
        default: () => []
      }
    },
    data() {
      return {
        dateRange: {startDate: null, endDate: null},
        statuses: [
          {key: i18n.t('Lotted'), value: 'lotted'},
          {key: i18n.t('Sold'), value: 'sold'},
          {key: i18n.t('Withdrawn'), value: 'withdrawn'},
          {key: i18n.t('Reserved'), value: 'reserved'},
          {key: i18n.t('Unsold'), value: 'unsold'},
          {key: i18n.t('Draft'), value: 'draft'},
          {key: i18n.t("Manually Sold"), value: 'manually_sold'},
        ],
        lotStatuses: [
          {key: i18n.t('Lotted'), value: 'lotted'},
          {key: i18n.t('Sold'), value: 'sold'},
          {key: i18n.t('Unsold'), value: 'unsold'},
          {key: i18n.t('Withdrawn'), value: 'withdrawn'},
          {key: i18n.t('Reserved'), value: 'reserved'},
        ],
        contractedStatus: [
          {key: 'returned', text: i18n.t('Returned')},
          {key: 'collected', text: i18n.t('Collected')},
        ],
        imagesValues: [
          {value: 'has_images', key: i18n.t('Has Images')},
          {value: 'without_images', key: i18n.t('Without Images')},
        ]
      }
    },
    computed: {
      ...mapState({
        searchModel: state => state.tableData[state.tableSource].searchModel,
        loading: state => state.tableData[state.tableSource].loading,
      }),
      ...unsoldProposalsMapState({
        suggestionOptions: state => state.suggestionOptions,
        proposalStatusOptions: state => state.proposalStatusOptions,
      }),
      ...globalMapState(['availableFilterFields', 'dynamicFieldsFilters', 'enableGroupedItems',
        'enableInventoryDraft', 'currentBranch', 'currentUser', 'itemLifecycleOptions']),
      usersOtherBranches() {
        if (!this.currentUser || !this.currentUser.branches) {
          return [];
        }
        return this.currentUser.branches.filter(b => b.uuid !== this.currentBranch.uuid);
      },
      inventoryDynamicFieldsFilters() {
        let result = [];

        if (this.dynamicFieldsFilters) {
          let inventoryDynamicFieldsFilters = this.dynamicFieldsFilters.inventories;
          if (inventoryDynamicFieldsFilters && inventoryDynamicFieldsFilters.length > 0) {
            result = inventoryDynamicFieldsFilters;
          }
        }

        return result;
      },
      receiptUuids() {
        if (this.searchModel && this.searchModel.receipts && this.searchModel.receipts.length > 0) {
          return this.searchModel.receipts.map(r => r.uuid)
        }
        return []
      },
      computedCurrencySymbol() {
        return this.currency ? this.currency.symbol : null;
      },
    },
    watch: {
      'searchModel.is_archived'() {
        if (!this.searchModel.is_archived) {
          this.searchModel.transferred_to_branch_uuid = null;
        }
      },
    },
    methods: {
      ...mapActions(['getTableData', 'updateSearchModel', 'resetTable', 'resetTableDataPaginationPage']),
      async onSearch() {
        await this.updateSearchModel(this.searchModel);
        this.resetTableDataPaginationPage();
        this.getTableData();
      },
      onResetClick() {
        this.dateRange = {startDate: null, endDate: null};
        this.resetTable();
      },
      async onDepartmentSelect(department) {
        this.updateSearchModel({department});
      },
      async onDepartmentUnselect() {
        this.updateSearchModel({department: null, department_uuid: null});
      },
      async onCategorySelect(category) {
        this.updateSearchModel({category});
      },
      onDateRangeApply() {
        this.searchModel.dateRange = this.dateRange
      },
      calculatePosition (dropdownList, component, {width, top, left, right}) {
        // which way the picker opens - "center", "left" or "right"
        if (component.opens === 'center') {
          if (left < width) {
            dropdownList.style.left = ((width - left) * 2 + (left + width / 2)) + 'px'
          } else {
            dropdownList.style.left = (left + width / 2) + 'px'
          }
          // console.log('center open', left, width)
        } else if (component.opens === 'left') {
          // console.log('left open', right, width)
          dropdownList.style.right = (window.innerWidth - right) + 'px'
        } else if (component.opens === 'right') {
          // console.log('right open')
          dropdownList.style.left = (left) + 'px'
        }
        dropdownList.style.top = top + 'px'
        // dropdownList.style.width = width + 'px'
      },
      async onCustomerSelect(customer) {
        this.updateSearchModel({customer});
      },
      async onCustomerUnselect() {
        this.updateSearchModel({
          customer: null,
          receipts: [],
          receipt_uuid: []
        })
      },
      async onUserSelect(user) {
        this.updateSearchModel({user});
      },
      async onReceiptSelect(receipt) {
        this.searchModel.receipts.push(receipt)
      },
      async onReceiptUnselect(receipt) {
        this.searchModel.receipts = this.searchModel.receipts.filter(r => r.uuid !== receipt.uuid)
      },
      onLatestAuctionSelect(auction) {
        this.searchModel.latest_auctions.push(auction)
      },
      getDynamicFieldFilterLabel(field) {
        if (field.type === 'checkbox') {
          return field.label;
        }
        return false;
      },
      async onSpecialistSelect(specialist) {
        this.updateSearchModel({specialist});
      },
      async onCreatedBySelect(created_by) {
        this.updateSearchModel({created_by});
      },
      async onRepUserSelect(rep_user) {
        this.updateSearchModel({rep_user});
      },
    },
  }
</script>

<style lang="scss" scoped>
</style>
